import { cloneDeep } from 'lodash';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import { removeObjectAtIndex } from '@utils/helper-utils';
import {
  IProposalBankerDetails,
  IProposalPageFormState,
} from '../../page/generic/form';
import { validateBankerRepeaterField } from '../../page/generic/validation';
import { isEmpty } from '@utils/validationUtils';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import CurrencyFormField from '@components/form-fields/CurrencyFormField';
import NumberInputFormField from '@components/form-fields/NumberInputFormField';
import { MAIN_THEME } from '@styles';

const initialValues = {
  initialError: {
    branchName: '',
    isMain: '',
    isAgency: '',
    numberOfEmployees: '',
    underwritingYear: '',
    infidelity: '',
    burglary: '',
    holdUp: '',
    inSafe: '',
    inTransit: '',
    forgedChecks: '',
    counterfeitCurrencies: '',
    clause6: '',
    falsification: '',
  },
  initialTouched: {
    branchName: false,
    isMain: false,
    isAgency: false,
    numberOfEmployees: false,
    underwritingYear: false,
    infidelity: false,
    burglary: false,
    holdUp: false,
    inSafe: false,
    inTransit: false,
    forgedChecks: false,
    counterfeitCurrencies: false,
    clause6: false,
    falsification: false,
  },
};

interface IBankersDetailsWidgetProps {
  pageState: IProposalPageFormState;
  onPageStateUpdate: (pageState: IProposalPageFormState) => void;
  disabledForm: boolean;
}

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderRadius: '4px',
    padding: '4px 2px 10px 25px',
    marginBottom: '14px',
  },
  containerWithoutClear: {
    width: '100%',
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderRadius: '4px',
    padding: '30px 2px 10px 25px',
    marginBottom: '14px',
  },
  section: {
    padding: '23px 0 25px',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: `repeat(4, 23.5%)`,
    gap: '2%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    alignContent: 'center',
  },
  addBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    cursor: 'pointer',
    padding: '0',
    margin: '20px auto 0',
    display: 'block',
  },
  removeBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    cursor: 'pointer',
  },
  clearBtn: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const BankersDetailsRepeaterWidget: React.FC<IBankersDetailsWidgetProps> = ({
  pageState,
  onPageStateUpdate,
  disabledForm,
}) => {
  const { classes } = useStyles();

  const values = pageState.values.bankersDetails;
  const errors = pageState.errors.bankersDetails;
  const touched = pageState.touched.bankersDetails;

  const onFieldBlur = (fieldName: string, rowIndex: number) => {
    const newPageState = cloneDeep(pageState);
    newPageState.touched.bankersDetails[rowIndex][fieldName] = true;
    onPageStateUpdate(newPageState);
  };

  const onFieldUpdate = (
    fieldName: keyof IProposalBankerDetails,
    value: IProposalBankerDetails[keyof IProposalBankerDetails],
    rowIndex: number,
    touched = false
  ) => {
    const newPageState = cloneDeep(pageState);

    (newPageState.values.bankersDetails[rowIndex] as any)[fieldName] = value;

    newPageState.errors.bankersDetails[rowIndex][fieldName] =
      validateBankerRepeaterField(
        fieldName,
        value,
        newPageState.values.bankersDetails[rowIndex]
      );
    if (touched) {
      newPageState.touched.bankersDetails[rowIndex][fieldName] = true;
    }
    onPageStateUpdate(newPageState);
  };

  const renderRow = (row: IProposalBankerDetails) => {
    const rowIndex = values.indexOf(row);

    return (
      <div
        className={
          values.length > 1 && !disabledForm
            ? classes.container
            : classes.containerWithoutClear
        }
      >
        <div className={classes.clearBtn}>
          {values.length > 1 && !disabledForm && (
            <button
              className={classes.removeBtn}
              onClick={() => onRowDelete(rowIndex)}
            >
              <ClearIcon fontSize="small" />
            </button>
          )}
        </div>
        <div className={classes.fieldRow}>
          <TextInputFormField
            key="branchName"
            name="branchName"
            title="Branch Name*"
            placeholder="Branch Name"
            value={values[rowIndex].branchName}
            error={
              touched[rowIndex].branchName ? errors[rowIndex].branchName : ''
            }
            onBlur={() => onFieldBlur('branchName', rowIndex)}
            onChange={(v) => {
              onFieldUpdate('branchName', v.target.value, rowIndex);
            }}
            disabled={disabledForm}
            material
          />

          <SelectFormField
            key="isMain"
            name="isMain"
            title="Main*"
            placeholder="Main"
            value={values[rowIndex].isMain}
            error={touched[rowIndex].isMain ? errors[rowIndex].isMain : ''}
            onBlur={() => onFieldBlur('isMain', rowIndex)}
            onChange={(v) => {
              onFieldUpdate('isMain', v, rowIndex);
            }}
            disabled={disabledForm}
            material
            selectOptions={{ YES: 'Yes', NO: 'No' }}
          />

          <SelectFormField
            key="isAgency"
            name="isAgency"
            title="Agency*"
            placeholder="Agency"
            value={values[rowIndex].isAgency}
            error={touched[rowIndex].isAgency ? errors[rowIndex].isAgency : ''}
            onBlur={() => onFieldBlur('isAgency', rowIndex)}
            onChange={(v) => {
              onFieldUpdate('isAgency', v, rowIndex);
            }}
            disabled={disabledForm}
            material
            selectOptions={{ YES: 'Yes', NO: 'No' }}
          />

          {/* Used Currency Form Field for the formatted number (comma separated) */}
          <CurrencyFormField
            key="numberOfEmployees"
            name="numberOfEmployees"
            title="Number of Employees*"
            placeholder="Number of Employees"
            value={values[rowIndex].numberOfEmployees || ''}
            error={
              touched[rowIndex].numberOfEmployees
                ? errors[rowIndex].numberOfEmployees
                : ''
            }
            onBlur={() => onFieldBlur('numberOfEmployees', rowIndex)}
            onChange={(v) => {
              onFieldUpdate('numberOfEmployees', v.target.value, rowIndex);
            }}
            maxDecimalPrecision={0}
            disabled={disabledForm}
            material={true}
          />

          <NumberInputFormField
            key="underwritingYear"
            name="underwritingYear"
            title="Underwriting Year*"
            placeholder="Underwriting Year"
            value={
              !isEmpty(values?.[rowIndex]?.underwritingYear)
                ? values[rowIndex].underwritingYear?.toString()
                : ''
            }
            error={
              touched[rowIndex].underwritingYear
                ? errors[rowIndex].underwritingYear
                : ''
            }
            onBlur={() => onFieldBlur('underwritingYear', rowIndex)}
            onChange={(v) => {
              onFieldUpdate(
                'underwritingYear',
                v.target.value?.toString(),
                rowIndex
              );
            }}
            disabled={disabledForm}
            material={true}
            maxDecimalPrecision={0}
          />

          <CurrencyFormField
            key="infidelity"
            name="infidelity"
            title="Infidelity*"
            placeholder="Infidelity"
            value={values[rowIndex].infidelity || ''}
            error={
              touched[rowIndex].infidelity ? errors[rowIndex].infidelity : ''
            }
            onBlur={() => onFieldBlur('infidelity', rowIndex)}
            onChange={(v) => {
              onFieldUpdate('infidelity', v.target.value, rowIndex);
            }}
            disabled={disabledForm}
            material
          />

          <CurrencyFormField
            key="burglary"
            name="burglary"
            title="Burglary*"
            placeholder="Burglary"
            value={values[rowIndex].burglary || ''}
            error={touched[rowIndex].burglary ? errors[rowIndex].burglary : ''}
            onBlur={() => onFieldBlur('burglary', rowIndex)}
            onChange={(v) => {
              onFieldUpdate('burglary', v.target.value, rowIndex);
            }}
            disabled={disabledForm}
            material
          />

          <CurrencyFormField
            key="holdUp"
            name="holdUp"
            title="Hold Up*"
            placeholder="Hold Up"
            value={values[rowIndex].holdUp || ''}
            error={touched[rowIndex].holdUp ? errors[rowIndex].holdUp : ''}
            onBlur={() => onFieldBlur('holdUp', rowIndex)}
            onChange={(v) => {
              onFieldUpdate('holdUp', v.target.value, rowIndex);
            }}
            disabled={disabledForm}
            material
          />

          <CurrencyFormField
            key="inSafe"
            name="inSafe"
            title="In Safe*"
            placeholder="In Safe"
            value={values[rowIndex].inSafe || ''}
            error={touched[rowIndex].inSafe ? errors[rowIndex].inSafe : ''}
            onBlur={() => onFieldBlur('inSafe', rowIndex)}
            onChange={(v) => {
              onFieldUpdate('inSafe', v.target.value, rowIndex);
            }}
            disabled={disabledForm}
            material
          />

          <CurrencyFormField
            key="inTransit"
            name="inTransit"
            title="In Transit*"
            placeholder="In Transit"
            value={values[rowIndex].inTransit || ''}
            error={
              touched[rowIndex].inTransit ? errors[rowIndex].inTransit : ''
            }
            onBlur={() => onFieldBlur('inTransit', rowIndex)}
            onChange={(v) => {
              onFieldUpdate('inTransit', v.target.value, rowIndex);
            }}
            disabled={disabledForm}
            material
          />

          <CurrencyFormField
            key="forgedChecks"
            name="forgedChecks"
            title="Forged Checks*"
            placeholder="Forged Checks"
            value={values[rowIndex].forgedChecks || ''}
            error={
              touched[rowIndex].forgedChecks
                ? errors[rowIndex].forgedChecks
                : ''
            }
            onBlur={() => onFieldBlur('forgedChecks', rowIndex)}
            onChange={(v) => {
              onFieldUpdate('forgedChecks', v.target.value, rowIndex);
            }}
            disabled={disabledForm}
            material
          />

          <CurrencyFormField
            key="counterfeitCurrencies"
            name="counterfeitCurrencies"
            title="Counter Feit Currencies*"
            placeholder="Counter Feit Currencies"
            value={values[rowIndex].counterfeitCurrencies || ''}
            error={
              touched[rowIndex].counterfeitCurrencies
                ? errors[rowIndex].counterfeitCurrencies
                : ''
            }
            onBlur={() => onFieldBlur('counterfeitCurrencies', rowIndex)}
            onChange={(v) => {
              onFieldUpdate('counterfeitCurrencies', v.target.value, rowIndex);
            }}
            disabled={disabledForm}
            material
          />

          <CurrencyFormField
            key="clause6"
            name="clause6"
            title="Clause 6*"
            placeholder="Clause 6"
            value={values[rowIndex].clause6 || ''}
            error={touched[rowIndex].clause6 ? errors[rowIndex].clause6 : ''}
            onBlur={() => onFieldBlur('clause6', rowIndex)}
            onChange={(v) => {
              onFieldUpdate('clause6', v.target.value, rowIndex);
            }}
            disabled={disabledForm}
            material
          />

          <CurrencyFormField
            key="falsification"
            name="falsification"
            title="Falsification*"
            placeholder="Falsification"
            value={values[rowIndex].falsification || ''}
            error={
              touched[rowIndex].falsification
                ? errors[rowIndex].falsification
                : ''
            }
            onBlur={() => onFieldBlur('falsification', rowIndex)}
            onChange={(v) => {
              onFieldUpdate('falsification', v.target.value, rowIndex);
            }}
            disabled={disabledForm}
            material
          />
        </div>
      </div>
    );
  };

  const onRowAdd = () => {
    const newPageState = cloneDeep(pageState);
    newPageState.values.bankersDetails.push({
      branchName: '',
      isMain: '',
      isAgency: '',
      numberOfEmployees: 0,
      underwritingYear: 0,
      infidelity: 0,
      burglary: 0,
      holdUp: 0,
      inSafe: 0,
      inTransit: 0,
      forgedChecks: 0,
      counterfeitCurrencies: 0,
      clause6: 0,
      falsification: 0,
    });

    newPageState.errors.bankersDetails.push({ ...initialValues.initialError });
    newPageState.touched.bankersDetails.push({
      ...initialValues.initialTouched,
    });

    onPageStateUpdate(newPageState);
  };

  const onRowDelete = (rowIndex: number) => {
    const newPageState = cloneDeep(pageState);

    newPageState.values.bankersDetails = removeObjectAtIndex(
      newPageState.values.bankersDetails,
      rowIndex
    );

    newPageState.errors.bankersDetails = removeObjectAtIndex(
      newPageState.errors.bankersDetails,
      rowIndex
    );
    newPageState.touched.bankersDetails = removeObjectAtIndex(
      newPageState.touched.bankersDetails,
      rowIndex
    );

    onPageStateUpdate(newPageState);
  };

  return (
    <WidgetPaper>
      <WidgetSection
        title="Bankers Blanket Bond Details"
        hasTitleSpecificDesign={false}
        classes={{
          container: classes.section,
        }}
        useSeparator={false}
      >
        {values.map((v) => renderRow(v))}

        {!disabledForm && (
          <button className={classes.addBtn} onClick={() => onRowAdd()}>
            <AddCircleOutlineIcon color="error" fontSize="small" />
          </button>
        )}
      </WidgetSection>
    </WidgetPaper>
  );
};

export default BankersDetailsRepeaterWidget;
